import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

// Helper condiviso di export/import Excel per i tab di data entry del Sistema
// Incentivante 2025 (target trimestrale RAP/CM, dati extra CM). Estratto in un
// servizio a sé per evitare di duplicare 5 volte la stessa plumbing XLSX/FileSaver
// nei 5 componenti tab (pattern di parsing ripreso da gce-contract.component.ts).
@Injectable({
  providedIn: 'root'
})
export class ExcelBulkService {

  // Esporta le righe passate su un unico foglio con intestazioni esplicite (headers).
  // Usando l'opzione "header" di XLSX.utils.json_to_sheet, anche con rows=[] viene
  // generato un file valido con la sola riga di intestazione: è il "file vuoto" da
  // compilare e reimportare quando la tabella non ha ancora dati.
  exportTemplate(rows: any[], headers: string[], fileName: string): void {
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(rows, { header: headers });
    const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const buffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: EXCEL_TYPE });
    FileSaver.saveAs(blob, fileName + EXCEL_EXTENSION);
  }

  // Legge il primo foglio di un file Excel e ritorna le righe come array di oggetti
  // (chiavi = intestazioni di colonna della prima riga).
  readFirstSheet(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
          resolve(rows as any[]);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    });
  }

  // Legge il primo foglio individuando da sola la riga di intestazione reale, invece
  // di assumere che sia la prima riga del foglio: serve per file come
  // dati_base_bonus.xlsx, che hanno una riga di titolo raggruppato (es. "Bonus
  // Quality" unita su più colonne) sopra la vera intestazione (es. "Codice",
  // "Clinica", "Mese", ...). Cerca, nelle prime maxHeaderRows righe, la prima che
  // contiene tutte le colonne richieste (requiredColumns, confronto case-insensitive)
  // e usa quella come intestazione per le righe sottostanti.
  readSheetWithHeaderDetection(file: File, requiredColumns: string[], maxHeaderRows: number = 5): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const grid: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as any[][];

          const requiredLower = requiredColumns.map(c => c.toLowerCase());
          let headerIndex = -1;
          let headerRow: any[] = [];
          for (let i = 0; i < Math.min(maxHeaderRows, grid.length); i++) {
            const rowLower = (grid[i] || []).map(c => String(c || '').trim().toLowerCase());
            if (requiredLower.every(rc => rowLower.includes(rc))) {
              headerIndex = i;
              headerRow = grid[i];
              break;
            }
          }
          if (headerIndex === -1) {
            reject(new Error('Intestazione non trovata: il file deve contenere le colonne ' + requiredColumns.join(', ')));
            return;
          }

          const rows: any[] = [];
          for (let i = headerIndex + 1; i < grid.length; i++) {
            const raw = grid[i] || [];
            if (raw.every(c => c === '' || c === null || c === undefined)) {
              continue;
            }
            const obj: any = {};
            headerRow.forEach((h, idx) => {
              const key = String(h || '').trim();
              if (key) {
                obj[key] = raw[idx] !== undefined ? raw[idx] : '';
              }
            });
            rows.push(obj);
          }
          resolve(rows);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    });
  }

  // Converte un valore data proveniente da Excel (seriale numerico, Date, stringa
  // gg/mm/aaaa o già ISO) in stringa YYYY-MM-DD, per i campi Mese/Trimestre.
  parseExcelDate(value: any): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'number') {
      const d = XLSX.SSF.parse_date_code(value);
      return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
    }
    if (value instanceof Date) {
      return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
    }
    const s = String(value).trim();
    const itMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (itMatch) {
      return `${itMatch[3]}-${itMatch[2].padStart(2, '0')}-${itMatch[1].padStart(2, '0')}`;
    }
    return s.substring(0, 10);
  }

  // Interpreta valori booleani "larghi" (SI/NO, TRUE/FALSE, 1/0, x) tipici di un
  // Excel compilato a mano, per campi come FerieSmaltiteOk.
  parseExcelBool(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    const s = String(value || '').trim().toLowerCase();
    return s === 'si' || s === 'sì' || s === 'true' || s === '1' || s === 'x' || s === 'yes';
  }

  wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
